import type { Notification } from '@travel-planner/shared/schemas';
import { useAuthData } from '@auth/features/auth';
import { useSupportDrawerStore } from '@support/entities/support-ticket';
import { useNavigate } from '@tanstack/react-router';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { toastStore } from '@/shared/ui/toast/toast-store';
import { useMarkRead } from '../api/use-mark-read';
import { useNotifications } from '../api/use-notifications';
import { useUnreadCount } from '../api/use-unread-count';
import { navigateToNotificationItinerary } from '../lib/navigate-to-itinerary';
import { presentNotification } from '../lib/present-notification';
import { observeNotificationIds } from './notification-toast-registry';
import { useNotificationDrawerStore } from './use-notification-drawer-store';

// THE single owner of notification polling for the whole app.
//
// Mounted once by `AppLayout`. Both bell triggers (desktop top bar + mobile
// header) and the drawer read from this context instead of calling the query
// hooks themselves — that is what guarantees ONE polling cadence. Two
// components calling `useQuery` with the same key share a cache entry but each
// register their own observer, and each observer schedules its own
// `refetchInterval` timer, which drift apart and roughly double the request
// rate. Consuming plain context values sidesteps that entirely.
//
// It also owns toast detection, for the same reason: the diff between "what we
// had" and "what just arrived" must happen in exactly one place, or two bells
// would raise two toasts for one notification.

type NotificationsContextValue = {
  /** False when the signed-in role has no notifications surface at all. */
  canRead: boolean;
  unreadCount: number;
  notifications: Notification[];
  isLoading: boolean;
  isError: boolean;
  isRefetching: boolean;
  refetch: () => void;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
};

const EMPTY: Notification[] = [];

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null
);

/**
 * Read the shared notification state. Returns an inert value outside a
 * provider so a component rendered in isolation (tests, the dev playground)
 * degrades to "nothing to show" instead of crashing.
 */
export const useNotificationsData = (): NotificationsContextValue =>
  useContext(NotificationsContext) ?? {
    canRead: false,
    unreadCount: 0,
    notifications: EMPTY,
    isLoading: false,
    isError: false,
    isRefetching: false,
    refetch: () => undefined,
    fetchNextPage: () => undefined,
    hasNextPage: false,
    isFetchingNextPage: false,
  };

export const NotificationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: authData } = useAuthData();
  const canRead =
    authData?.user.permissions.includes('notifications:read') ?? false;

  const countQuery = useUnreadCount(canRead);
  const listQuery = useNotifications(canRead);
  const navigate = useNavigate();
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);
  const openDrawerWith = useNotificationDrawerStore((s) => s.openDrawerWith);
  // `mutate` is stable across renders; the mutation object is not, and this
  // effect must not re-run on every poll.
  const { mutate: markRead } = useMarkRead();

  // Structural sharing keeps `listQuery.data` referentially stable across a
  // refetch that changed nothing, so this memo keeps the toast effect from
  // re-running on every poll.
  const notifications = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.data) ?? EMPTY,
    [listQuery.data]
  );

  const isListReady = listQuery.isSuccess;

  useEffect(() => {
    if (!isListReady) return;

    // Seeding happens inside the registry: the first observation of the
    // session records everything silently, so history never toasts.
    const freshIds = new Set(
      observeNotificationIds(notifications.map((row) => row.id))
    );
    if (freshIds.size === 0) return;

    const arrived = notifications.filter(
      (row) => freshIds.has(row.id) && row.readOn === null
    );
    if (arrived.length === 0) return;

    const [newest] = arrived;
    if (!newest) return;

    // One toast per detection pass. The store shows a single toast at a time,
    // so a burst is summarised rather than fighting over the slot.
    if (arrived.length > 1) {
      toastStore.getState().show({
        title: `${arrived.length} new notifications`,
        message: 'Recent activity on your trips.',
        placement: 'top-right',
        action: { label: 'View all', onClick: openDrawer },
      });
      return;
    }

    // The toast announces that something happened; it is not a second copy of
    // the notification card. Short event title, one plain line, one action.
    const {
      headline,
      description,
      itineraryId,
      itineraryView,
      supportTicketId,
    } = presentNotification(newest);

    toastStore.getState().show({
      title: headline,
      message: description,
      placement: 'top-right',
      // "View details" continues the flow rather than teleporting, and is
      // exactly equivalent to expanding the card in the drawer: it marks the
      // notification read (optimistically, so the badge and the list update on
      // click rather than at the next poll), opens the drawer with it already
      // expanded, and continues to whatever the notification points at — the
      // trip's page, or the support drawer opened on the ticket.
      action: {
        label: 'View details',
        onClick: () => {
          markRead({ id: newest.id });
          openDrawerWith(newest.id);
          if (itineraryId) {
            navigateToNotificationItinerary(
              navigate,
              itineraryId,
              itineraryView
            );
          } else if (supportTicketId) {
            useSupportDrawerStore
              .getState()
              .openDrawerWithTicket(supportTicketId);
          }
        },
      },
    });
  }, [
    isListReady,
    notifications,
    markRead,
    navigate,
    openDrawer,
    openDrawerWith,
  ]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      canRead,
      unreadCount: countQuery.data ?? 0,
      notifications,
      isLoading: listQuery.isLoading,
      isError: listQuery.isError,
      isRefetching: listQuery.isRefetching,
      refetch: () => {
        void listQuery.refetch();
      },
      fetchNextPage: () => {
        void listQuery.fetchNextPage();
      },
      hasNextPage: listQuery.hasNextPage,
      isFetchingNextPage: listQuery.isFetchingNextPage,
    }),
    [
      canRead,
      countQuery.data,
      notifications,
      listQuery.isLoading,
      listQuery.isError,
      listQuery.isRefetching,
      listQuery.refetch,
      listQuery.fetchNextPage,
      listQuery.hasNextPage,
      listQuery.isFetchingNextPage,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
